import { createRoomApi } from "../../room";
import { autoUploadLatest } from "../../upload";
import { createFieldApi } from "../../../lib/api/fieldApi";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SessionGrant } from "../../../types/session";
import { isSupported } from "../../../lib/boot/device";
import { useBootDeps } from "../../../lib/boot/deps";
import { LoadingSteps } from "../../../components/LoadingSteps";
import type { Step, StepState } from "../../../components/LoadingSteps";
import { ScreenRoot } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { StatusScreen } from "../../../components/StatusScreen";
import type { BannerAction } from "../../../components/FeBanner";
import type { ErrorCode } from "../../../lib/http/errors";
import { useT } from "../../../i18n";
import type { Audience } from "../../../i18n/fe";
import { useSession, useSessionManager } from "../../../store/session/SessionProvider";
import { homeFor } from "../../../constants/paths";
import { useCampusSsoMessage } from "../lib/campusSso";
import { peekPendingReturn } from "../../../lib/bench/handoff";
import { peekLaunchNext, takeLaunchNext } from "../../../store/session/launchNext";
import type { Door, StepId } from "../types";

/** 와이어 FS-01-01: '부품 ✓ · 내 기계 ✓ · 친구 작품 …' — 세션 수립은 단계로 보이지 않는다(진행 바 0) */
const STUDENT_STEPS: readonly [StepId, string][] = [
  ["assets", "student.launch.stepAssets"],
  ["machine", "student.launch.stepMachine"],
  ["works", "student.launch.stepFriends"],
];
/** Campus 미션 입장(next=course) — 마지막에 내 로봇을 올린다 */
const MISSION_STEPS: readonly [StepId, string][] = [...STUDENT_STEPS, ["upload", "student.launch.stepUpload"]];
const TEACHER_STEPS: readonly [StepId, string][] = [
  ["session", "teacher.launch.stepSession"],
  ["assets", "teacher.launch.stepAssets"],
  ["works", "teacher.launch.stepClassWorks"],
];

/** 문 B 에서 BFF 토큰을 기다리는 최대 시간 — 넘으면 FE-304(다시 열기) */
export const SSO_WAIT_MS = 20_000;

/**
 * FS-01-01 런치 로딩 — 클릭 0 자동 진행(SCR v0.6).
 * 순서: 세션(문 A 티켓 교환 / 문 B BFF 토큰) → SE/SU 판정(세션 클레임) → 기기 감지(FE-601)
 *       → 자산 캐시 → 스냅샷(내 기계 · 친구 작품 / 교사: 반 작품) → 역할별 홈.
 * 세션 오류 → FS-C1-02(FE-304) · 코스 경계 → FS-C1-03(FE-302) · 미지원 기기 → FS-C1-04(FE-601).
 */
export function EntryScreen({ door }: { door: Door }) {
  const t = useT();
  const navigate = useNavigate();
  const deps = useBootDeps();
  const manager = useSessionManager();
  const state = useSession();
  const [progress, setProgress] = useState<StepId | "done">("session");
  const [bootError, setBootError] = useState<ErrorCode | null>(null);
  const [ssoTimedOut, setSsoTimedOut] = useState(false);
  // Campus 미션 입장인가 — 단계 표시에 '내 로봇 올리기'를 더한다(값은 부트스트랩이 꺼낸다)
  const [missionEntry] = useState(() => peekLaunchNext() === "course");

  const session: SessionGrant | null = state.status === "ready" ? state.session : null;
  const role =
    session?.role === "operator" || session?.role === "org_admin" || session?.role === "observer"
      ? "teacher"
      : (session?.role ?? "student");
  const audience: Audience = role;

  // 문 B: postMessage 로 BFF 토큰 받기
  const waitingSso = door === "B" && state.status === "none" && !ssoTimedOut;
  useCampusSsoMessage(waitingSso, (token) => void manager.signInWithBffToken(token));
  useEffect(() => {
    if (!waitingSso) return;
    const h = setTimeout(() => setSsoTimedOut(true), SSO_WAIT_MS);
    return () => clearTimeout(h);
  }, [waitingSso]);

  // 세션이 서면 부트스트랩
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      if (!isSupported(deps.detectDevice())) {
        setBootError("FE-601");
        return;
      }
      setProgress("assets");
      await deps.loadAssets(session);
      if (cancelled) return;
      if (session.role === "student") {
        setProgress("machine");
        await deps.loadMyMachine(session);
        if (cancelled) return;
      }
      setProgress("works");
      await deps.loadWorks(session);
      if (cancelled) return;
      // 4.6 §4.3 — Campus 미션 입장(`next=course`)이면 룸보다 먼저(학생만 · 한 번 쓰고 지운다).
      // 2026-11-03 사용자 요청: 입구 → 새로 올리기 → 다음 → 올리기 → 입구로 → 코스 도전이 너무 길다 — 최근 조립 + 코딩을 자동으로 올리고
      // 미션 코스(정적 기계면 CourseScreen 이 무대로)로 곧장 간다. 올릴 것이 없거나 검사에 걸리면 올리기 화면(이유가 보인다) · 통신 오류면 입구(예전 길).
      const next = takeLaunchNext();
      if (next === "course" && session.role === "student") {
        setProgress("upload");
        const r = await autoUploadLatest(createFieldApi(manager.request, session.courseId)).catch(() => null);
        if (cancelled) return;
        setProgress("done");
        if (!r) navigate("/home", { replace: true });
        else if (r.kind === "none" || r.kind === "invalid") navigate("/upload", { replace: true });
        else navigate("/course", { replace: true, state: { mission: true } });
        return;
      }
      setProgress("done");
      // micro:bit 놀이터 미션(PICNIC-MB-FIELD-001 §7 결정 3) — 코스 대신 FS-MB
      if (next === "microbit" && session.role === "student") {
        navigate("/microbit", { replace: true });
        return;
      }
      // Campus 운영 콘솔 '무대 꾸밈'(2026-10-08) — 운영자는 FS-15-07 로
      if (next === "stages" && session.role === "operator") {
        navigate("/ops/stages", { replace: true });
        return;
      }
      // FS-01 자동 입장(클릭 0 · SR-ROOM-01): 학생은 열린 룸이 있으면 룸으로
      const room =
        session.role === "student"
          ? await createRoomApi(manager.request, session.courseId)
              .room()
              .catch(() => null)
          : null;
      if (cancelled) return;
      navigate(room?.joinToken ? "/room" : homeFor(session.role), { replace: true });
    })().catch(() => {
      if (!cancelled) setBootError("NET-SERVER");
    });
    return () => {
      cancelled = true;
    };
  }, [session, deps, navigate, manager]);

  const steps: Step[] = useMemo(() => {
    const defs = role === "teacher" ? TEACHER_STEPS : missionEntry ? MISSION_STEPS : STUDENT_STEPS;
    const order = defs.map(([id]) => id);
    // 학생 단계에는 세션이 없다 — 세션 중이면 첫 단계(부품)가 '하는 중'
    const cur = progress === "done" ? order.length : Math.max(0, order.indexOf(progress));
    return defs.map(([id, key], i) => {
      const st: StepState = i < cur ? "done" : i === cur ? "active" : "waiting";
      return { id, label: t(key), state: st };
    });
  }, [role, progress, t, missionEntry]);

  // ── 상태 화면(FS-C1) ─────────────────────────────────────────
  const errorCode: ErrorCode | null =
    bootError ??
    (state.status === "error"
      ? state.code
      : state.status === "none" && (door === "A" || ssoTimedOut)
        ? "FE-304"
        : null);

  if (errorCode) {
    const actions: BannerAction[] = [];
    if (state.status === "error" && state.retryable)
      actions.push({ label: t("common.retry"), icon: "↻", onClick: () => void manager.retry() });
    else if (errorCode === "FE-304" || errorCode === "FE-301" || errorCode === "FE-302")
      actions.push({ label: t("common.toCampus"), icon: "↩", onClick: deps.backToCampus });
    // Bench 에서 돌아왔는데(폴백) 이 탭에 세션이 없다 — 캠퍼스(문 B)로 다시 들어오면 룸 화면이 맡긴 저장본을 가져온다
    const benchBack = errorCode === "FE-304" && state.status === "none" && !!peekPendingReturn();
    return (
      <>
        <StatusScreen
          code={errorCode}
          audience={audience}
          actions={actions}
          note={
            benchBack ? (
              <p className="pf-muted" data-testid="bench-return-note" role="status">
                <b>{t("student.launch.benchReturn.title")}</b>{" "}
                {t("student.launch.benchReturn.text")}
              </p>
            ) : null
          }
        />
        {deps.devPanel}
      </>
    );
  }

  return <Loading door={door} role={role} waitingSso={door === "B" && !session} steps={steps} />;
}

/**
 * FS-01-01 로딩 틀 — 와이어: '들어가는 중' · ⏳ 피코(로딩 애니메이션) 옆에 진행 바와 단계(부품 ✓ · 내 기계 ✓ · 친구 작품 …).
 * 상단 바는 역할별 홈의 제목(학생 '놀이터 입구' · 교사 'Field · 교사').
 */
function Loading({
  door,
  role,
  waitingSso,
  steps,
}: {
  door: Door;
  role: "student" | "teacher";
  waitingSso: boolean;
  steps: Step[];
}) {
  const t = useT();
  const titleKey = role === "teacher" ? "teacher.launch.title" : "student.launch.title";
  useTopBar(
    role === "teacher"
      ? { icon: "👩‍🏫", title: t("teacher.home.appTitle") }
      : { icon: "🌿", title: t("student.home.title") },
  );
  return (
    <ScreenRoot id="FS-01-01" title={t(titleKey)} className="pf-pane pf-pane--hero pf-launch">
      <div className="pf-launch__row" aria-busy data-door={door}>
        <div className="pf-pico pf-pico--busy" data-pico="busy">
          <div className="pf-pico__avatar" aria-hidden="true">
            ⏳
          </div>
        </div>
        <div className="pf-launch__progress">
          <LoadingSteps steps={steps} label={t(titleKey)} />
        </div>
      </div>
      {waitingSso && (
        <p className="pf-muted" role="status">
          {t("student.launch.picoSso")}
        </p>
      )}
    </ScreenRoot>
  );
}
